import React, { Fragment } from "react";
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import Autocomplete from "../../Shared/Components/Autocomplete";
import { ProfileContext } from "../../Auth/ProfileProvider";
import { AuthContext } from "../../Auth/AuthProvider";
import { PreCertTable } from "../../Shared/Components/PreCertTable";
import { deepCopy } from "../../Shared/Utility/DeepCopy";
import { getDateWithOffset } from "../../Shared/Utility/GetDateWithOffset";
import { ValidatorHelper } from "../../Shared/Utility/ValidatorHelper";
import { abpnClient } from "../../Services/AbpnClient";
import { TrainingProgram, TrainingProgramTypes } from "../../Shared/Models/TrainingProgram";
import { EvaluatorSearchDto, PhysicianSearchResult } from "../../Shared/Models/PhysicianSearchResult";
import { ClinicalSkill, ClinicalSkillTypeLookup } from "../Models/ClinicalSkill";
import { SourceType } from "../Models/SourceType";
import { PostGraduateYear } from "../Models/PostGraduateYear";
import { KnownCertificationType } from "../Models/EligibilityRequirements";

interface IClinicalSkillEvaluationsGridProps {
    physicianId: number;
    clinicalSkills: Array<ClinicalSkill>;
    isReadonly: boolean;
    saveResident: (postGraduateYears: Array<PostGraduateYear>, clinicalSkills: Array<ClinicalSkill>, isOnFastTrack: boolean) => void;
    isAlumni: boolean;
    programTypeIdsWithApprovedApplication: Array<number>;
    setIsEditing: (value: boolean) => void;
}

interface IClinicalSkillsData {
    clinicalSkills: Array<ClinicalSkill>,
    originalClinicalSkills: Array<ClinicalSkill>,
    filteredCseTypeListByProgram: Array<ClinicalSkillTypeLookup>
}

export function ClinicalSkillEvaluationsGrid(props: IClinicalSkillEvaluationsGridProps) {
    const { user } = React.useContext(ProfileContext);
    const authContext = React.useContext(AuthContext);

    const [clinicalSkillTypeLookupList, setClinicalSkillTypeLookupList] = React.useState<Array<ClinicalSkillTypeLookup>>([]);
    const [data, setData] = React.useState<IClinicalSkillsData>({clinicalSkills: [], originalClinicalSkills: [], filteredCseTypeListByProgram: []})
    const [programList, setProgramList] = React.useState<Array<TrainingProgram>>([]);
    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false);
    const [csToDelete, setCsToDelete] = React.useState<ClinicalSkill>();
    const [editingLegacyCse, setEditingLegacyCse] = React.useState<boolean>(false);
    const [canAddClinicalSkill, setCanAddClinicalSkill] = React.useState<boolean>(false);
    const [attestationValidationErrors, setAttestationValidationErrors] = React.useState<Array<string>>([]);
    const [showInvalidAttestationAlert, setShowInvalidAttestationAlert] = React.useState<boolean>(false);

    const userSelectedTrainingProgram = user.trainingPrograms.find(_ => _.trainingProgramId === user.selectedTrainingProgramId);
    const clinicalSkillsForm: string = "clinicalSkillsForm";
    const passedDateFormId: string = "passedDate";
    const evaluationFormId: string = "evaluationInput";
    const evaluatorInputFormName: string = "evaluatorInput";
    const editForm = React.useRef(null);

    React.useEffect(() => {
        let updatedData = deepCopy(data);
        updatedData.clinicalSkills = sortClinicalSkills(deepCopy(props.clinicalSkills));
        updatedData.originalClinicalSkills = sortClinicalSkills(deepCopy(props.clinicalSkills));

        abpnClient.get<Array<TrainingProgram>>("/api/trainingProgram/getTrainingPrograms", authContext)
                  .then((trainingProgramList: TrainingProgram[]) => setProgramList(trainingProgramList))

        abpnClient.get<Array<ClinicalSkillTypeLookup>>(`/api/training/GetClinicalSkillTypeLookupList`, authContext)
                  .then((lookupList: ClinicalSkillTypeLookup[]) => {
                        setClinicalSkillTypeLookupList(lookupList);
                        setCanAddClinicalSkill(false);

                        let filteredCseLookupListByProgramType: Array<ClinicalSkillTypeLookup> = [];
                        lookupList.filter(_ => _.programTypeId === userSelectedTrainingProgram.trainingProgramTypeId && _.isActive).map(_ => {
                            if (!filteredCseLookupListByProgramType.find(x => x.id === _.id)) filteredCseLookupListByProgramType.push(_);
                        })
                        updatedData.filteredCseTypeListByProgram = filteredCseLookupListByProgramType;

                        if (userSelectedTrainingProgram.trainingProgramTypeId === TrainingProgramTypes.PsychiatryNeurology) {
                            let psychCseType = filteredCseLookupListByProgramType.find(_ => _.evaluatorCertificationIds.find(id => id === KnownCertificationType.Psychiatry));
                            let updatedCseTypeList = filteredCseLookupListByProgramType.filter(_ => _ !== psychCseType);
                            updatedCseTypeList.unshift(psychCseType);
                            updatedData.filteredCseTypeListByProgram = updatedCseTypeList;
                        }
                        setData(updatedData);

                        const matches = lookupList.filter(x => x.programTypeId === userSelectedTrainingProgram.trainingProgramTypeId);

                        if (matches.length === 0)
                            return;

                        const limit = matches[0].limit;

                        if (limit === null) {
                            setCanAddClinicalSkill(true);
                            return;
                        }

                        const lookupListIds = matches.map(x => x.id);
                        const currentMatchingCSEs = props.clinicalSkills.filter(x => lookupListIds.includes(x.clinicalSkillTypeId));

                        if (currentMatchingCSEs.length <= limit)
                            setCanAddClinicalSkill(true);
                })

        return () => { setProgramList(null); setClinicalSkillTypeLookupList(null); setCanAddClinicalSkill(null);}
    }, [props.clinicalSkills]);

    const sortClinicalSkills = (skills: Array<ClinicalSkill>) => {
        return skills.sort((a, b) => new Date(a.completedDate).valueOf() - new Date(b.completedDate).valueOf());
    }

    const updateClinicalSkill = (updateFunction: (n: ClinicalSkill) => void): ClinicalSkill => {
        let updatedData = deepCopy(data);
        let clinicalSkill = updatedData.clinicalSkills.find(_ => _.inEditMode);
        updateFunction(clinicalSkill);

        setData(updatedData);
        return clinicalSkill;
    }

    const updateCompletedDate = (newDate: Date) => {
        updateClinicalSkill((_: ClinicalSkill) => {
            _.completedDate = newDate ? newDate.toJSON() : null;
            _.evaluatorName = null;
            _.evaluatorId = null;
            _.evaluator = null;
            clearDateValidation();
        });
    }

    const clearDateValidation = () => {
        let csPassedDateElement: HTMLInputElement = document.getElementById(passedDateFormId) as HTMLInputElement;
        ValidatorHelper.clearCustomComponentValidation(csPassedDateElement);
    }

    const updateIsAttested = () => {
        updateClinicalSkill((_: ClinicalSkill) => {
            _.isAttestedComplete = !_.isAttestedComplete;
        })
    }

    const saveCseAttestation = async (cse: ClinicalSkill) => {
        if(!cse.inEditMode){
            let validationErrors = [];
            const form = editForm.current;
            cse.isAttestedComplete = true;


            if (cse.completedDate && new Date(cse.completedDate) > new Date()) {
                validationErrors.push("CSE must have a Passed Date prior to the attestation date.");
            }

            if(cse.evaluatorName && cse.evaluatorId === null) {
                validationErrors.push("Reselection of evaluator is required");
                if(!clinicalSkillTypeLookupList.some(_ => _.id === cse.clinicalSkillTypeId && _.isActive)) {
                    validationErrors.push("Reselection of CSE is required");
                }
            }

            if(validationErrors.length > 0) {
                setAttestationValidationErrors(validationErrors);
                setShowInvalidAttestationAlert(true);
                startEdit(cse);
            }
            else{
                if (form.checkValidity()) {
                    abpnClient.post<ClinicalSkill>(`/api/training/saveClinicalSkill`, cse, authContext).then(updatedCS => {
                        let updatedData = deepCopy(data);
                        let updatedCSs = updatedData.clinicalSkills;
                        let editIndex = updatedCSs.findIndex(_ => _.inEditMode);
                        updatedCSs[editIndex] = updatedCS;
                        updatedData.clinicalSkills = updatedCSs;
                        updatedData.originalClinicalSkills = updatedCSs;
                        checkCseToBeEdited(null, false);
                        setData(updatedData);
                        props.saveResident(null, updatedData.clinicalSkills, null);
                        form.classList.remove('was-validated');
                    });
                }
            }

        } else {
            updateIsAttested();
        }
    }

    const saveClinicalSkill = () => {
        const form = editForm.current;
        var cs = data.clinicalSkills.find(_ => _.inEditMode);
        setAttestationValidationErrors([]);
        setShowInvalidAttestationAlert(false);

        validateDates(cs);

        form.classList.add('was-validated');

        if(editingLegacyCse){
            validateEvaluationInput(cs);
            validateEvaluatorInput(cs);
        }

        if (form.checkValidity()) {
            abpnClient.post<ClinicalSkill>(`/api/training/saveClinicalSkill`, cs, authContext).then(updatedCS => {
                let updatedData = deepCopy(data);
                let updatedCSs = updatedData.clinicalSkills;
                let editIndex = updatedCSs.findIndex(_ => _.inEditMode);
                updatedCSs[editIndex] = updatedCS;
                updatedData.clinicalSkills = updatedCSs;
                updatedData.originalClinicalSkills = updatedCSs;
                checkCseToBeEdited(null, false);
                setData(updatedData);
                props.saveResident(null, updatedData.clinicalSkills, null);
                form.classList.remove('was-validated');
                props.setIsEditing(false);
            });
        }
    }

    const validateEvaluationInput = (cs: ClinicalSkill) => {
        let element = document.getElementById(evaluationFormId) as HTMLInputElement;
        let isValid = clinicalSkillTypeLookupList.some(_ => _.id === cs.clinicalSkillTypeId && _.isActive);
        if (!isValid) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Reselection of CSE is required");
        }
        else {
            ValidatorHelper.clearCustomValidation(element);
        }
    }

    const validateEvaluatorInput = (cs: ClinicalSkill) => {
        let element = document.getElementsByName(evaluatorInputFormName)[0] as HTMLInputElement;
        if (cs.evaluatorId === null) {
            ValidatorHelper.setCustomComponentValidation(element, "Reselection of evaluator is required");
        } else {
            ValidatorHelper.clearCustomComponentValidation(element);
        }
    }

    const validateDates = (cs: ClinicalSkill) => {
        if (!cs.completedDate || !cs.isAttestedComplete) return;

        let csPassedDateElement: HTMLInputElement = document.getElementById(passedDateFormId) as HTMLInputElement;

        if (new Date(cs.completedDate) > new Date()) {
            ValidatorHelper.setCustomComponentValidation(csPassedDateElement, "CSE must have a Passed Date prior to the attestation date.");
            return;
        }
    }

    const addClinicalSkill = async() => {
        let newCS = new ClinicalSkill(props.physicianId, user.selectedTrainingProgramId);
        newCS.trainingProgramName1 = userSelectedTrainingProgram.trainingProgramName1;
        newCS.trainingProgramCode = userSelectedTrainingProgram.trainingProgramCode;

        let updatedData = deepCopy(data);
        updatedData.clinicalSkills.push(newCS);

        setData(updatedData);
    }

    const deleteClinicalSkill = async() => {
        setIsConfirmingDelete(false);
        abpnClient.post<ClinicalSkill>(`/api/training/deleteClinicalSkill`, csToDelete, authContext).then(() => {
            removeDeletedClinicalSkill(csToDelete.clinicalSkillId);
            setCsToDelete(null);
        });
    }

    const confirmDeleteClinicalSkill = (cs: ClinicalSkill, isConfirming: boolean) => {
        setCsToDelete(cs);
        setIsConfirmingDelete(isConfirming);
    }

    const removeDeletedClinicalSkill = (clinicalSkillId: number) => {
        let updatedData = deepCopy(data);
        let currentClinicalSkills = updatedData.clinicalSkills;
        var updatedClinicalSkills = currentClinicalSkills.filter(_ => _.clinicalSkillId !== clinicalSkillId);
        updatedData.clinicalSkills = updatedClinicalSkills;
        updatedData.originalClinicalSkills = updatedClinicalSkills;
        setData(updatedData);
        props.saveResident(null, updatedData.clinicalSkills, null);
    }

    const checkCseToBeEdited = (cs: ClinicalSkill, editing: boolean) => {
        if (editing) {
            if(cs.evaluatorName && cs.evaluatorId === null){
                setEditingLegacyCse(true);
            }
        }
        else {
            setEditingLegacyCse(false);
        }
    }

    const startEdit = async(cs: ClinicalSkill) => {
        props.setIsEditing(true);
        checkCseToBeEdited(cs, true);
        let updatedData = deepCopy(data);
        let updatedCSEs = updatedData.clinicalSkills;
        let editIndex = updatedData.clinicalSkills.findIndex(_ => _.clinicalSkillId === cs.clinicalSkillId);
        updatedCSEs[editIndex] = cs;

        if (!updatedData.filteredCseTypeListByProgram.find(_ => _.id === cs.clinicalSkillTypeId)) {
            updatedCSEs[editIndex].clinicalSkillTypeId = null;
        }
        updatedCSEs[editIndex].inEditMode = true;
        setData(updatedData);
    }

    const cancelEdit = () => {
        props.setIsEditing(false);
        setAttestationValidationErrors([]);
        setShowInvalidAttestationAlert(false);
        checkCseToBeEdited(null, false);
        const form = document.getElementById(clinicalSkillsForm) as HTMLFormElement;
        form.classList.remove('was-validated');
        let updatedData = deepCopy(data);
        updatedData.clinicalSkills = updatedData.originalClinicalSkills;
        setData(updatedData);
    }

    const updateClinicalSkillType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let element = document.getElementById(evaluationFormId) as HTMLInputElement;
        ValidatorHelper.clearCustomValidation(element);
        updateClinicalSkill((_: ClinicalSkill) => {
            _.clinicalSkillTypeId = e.target.value ? parseInt(e.target.value) : null;
            _.evaluatorName = null;
            _.evaluatorId = null;
            _.evaluator = null;
        });

      };


    const updateEvaluator = (selectedItem: PhysicianSearchResult) => {
        if (selectedItem) {
            updateClinicalSkill((_: ClinicalSkill) => {
                _.evaluatorId = selectedItem.physicianId;
                _.evaluator = selectedItem;
                _.evaluatorName = null;
            });
        }
        clearEvaluatorValidation();
    }

    const clearEvaluatorValidation = () => {
        let element = document.getElementsByName(evaluatorInputFormName)[0] as HTMLInputElement;
        ValidatorHelper.clearCustomComponentValidation(element);
    }

    const findEvaluators = (filter: string, cs: ClinicalSkill): any => {
        if (!filter || filter.trim().length < 3)  return null;

        let program = programList.find(_ => _.trainingProgramId === cs.trainingProgramId);
        let lookupItem = clinicalSkillTypeLookupList.find(_ => _.programTypeId === program.trainingProgramTypeId && _.isActive && _.id === cs.clinicalSkillTypeId);
        let evaluatorSearch = new EvaluatorSearchDto();
        evaluatorSearch.certificationIds = lookupItem.evaluatorCertificationIds;
        evaluatorSearch.name = filter;
        evaluatorSearch.certifiedOn = cs.completedDate;

        return abpnClient.post<Array<PhysicianSearchResult>>('/api/training/findEvaluators', evaluatorSearch, authContext);
    }

    const getDefaultEvaluator = (cs: ClinicalSkill) => {
        if (!cs.evaluator && !cs.evaluatorName?.length) return null;

        if (!cs.evaluator && cs.evaluatorName?.length) {
            let defaultEvaluator = new PhysicianSearchResult();
            defaultEvaluator.fullName = cs.evaluatorName;
            return defaultEvaluator;
        }

        return cs.evaluator;
    }

    const getEvaluatorDisplayName = (item: PhysicianSearchResult) => {
        if (item?.physicianId) return `${item?.firstName} ${item?.lastName} - ${item?.stateCode}`;

        if (item?.fullName?.length) return `${item.fullName}`;

        return '';
    }

    const getRowProps = (row: any) => {
        const { original: skill }: {original: ClinicalSkill} = row;

        return skill.source === SourceType.ABPN ? { className: 'pc-table-row-abpn-source' } : { };
    }

    const isEditButtonHidden = (cs: ClinicalSkill) => {
        return props.isReadonly
        || data.clinicalSkills.find(_ => _.inEditMode) != null
        || cs.source === SourceType.ABPN
        || (cs.isAttestedComplete && user?.userRole?.isProgramCoordinator)
        || (cs.ownedByTrainingProgramId !== user?.selectedTrainingProgramId)
        || props.isAlumni;
    }

    const isDeleteButtonHidden = (cs: ClinicalSkill) => {
        return props.isReadonly
        || data.clinicalSkills.find(_ => _.inEditMode) != null
        || cs.source === SourceType.ABPN
        || (cs.isAttestedComplete && user?.userRole?.isProgramCoordinator)
        || (cs.ownedByTrainingProgramId !== user?.selectedTrainingProgramId)
        || props.isAlumni;
    }

    const renderCseTeleDocInfo = () =>{
        switch(userSelectedTrainingProgram.trainingProgramTypeId){
            case TrainingProgramTypes.Psychiatry:
                return(
                    <Fragment>
                        <p><strong>By attesting to the completion of the clinical skills evaluations, I affirm that they adhere to the following statement:</strong></p>
                        <div>
                            <p>A maximum of one of the three evaluations may be completed with an actual patient via live video streaming if the clinical setting uses telehealth as part of its patient care offerings and if the patient is clinically appropriate to be seen via telehealth. 
                                <i>For all CSEs, please note that the ABPN-certified psychiatrist assessors must be physically present in the same room as the trainee for the entire patient evaluation, whether the patient is seen in person or remotely. As well, all patient evaluation 
                                    must be with a live actual patient; videorecorded interactions or simulated/standardized patients cannot be used as the basis for the clinical skills evaluation.  The full CSE requirements document is located on the <a href="https://www.abpn.org/wp-content/uploads/2023/04/CSE-Psychiatry.pdf" target="_blank">ABPN website</a>.</i></p>
                        </div>
                    </Fragment>);
            case TrainingProgramTypes.Neurology:
                case TrainingProgramTypes.ClinicalNeurophysiology:
                return(
                    <Fragment>
                        <p><strong>By attesting to the completion of the clinical skills evaluations, I affirm that they adhere to the following statement:</strong></p>
                        <div>
                            <p>* The episodic/ambulatory patient CSE may be completed with an actual patient via live video streaming if the clinical setting uses telehealth as part of its patient care offerings and if the ambulatory patient is clinically appropriate to be seen via 
                                telehealth (e.g., an established patient whose evaluation does not require physical examination elements that cannot be done remotely). The other CSEs must be completed with an actual patient in person; thus, a maximum of one out of the five required 
                                CSEs may be completed via a telehealth patient evaluation.</p>
                            <p><i>For all CSEs,</i> please note that the ABPN-certified neurologist/child neurologist assessor must be physically present in the same room as the trainee for the entire patient evaluation, whether the patient is seen in person or remotely. As well,
                             all patient evaluations must be with a live actual patient; videorecorded interactions or simulated/standardized patients cannot be used as the basis for the clinical skills evaluation.  The full CSE requirements document is located on the <a href="https://www.abpn.org/wp-content/uploads/2023/04/CSE-Neurology.pdf" target="_blank">ABPN website</a>.</p>
                        </div>
                    </Fragment>);
            case TrainingProgramTypes.Cap:
                return(
                    <Fragment>
                        <p><strong>By attesting to the completion of the clinical skills evaluations, I affirm that they adhere to the following statement:</strong></p>
                        <div>
                            <p>A maximum of one evaluation (1 of 5 evaluations for P/CAP fast-track training programs or 1 of 3 evaluations for CAP two-year training programs) may be completed with an actual 
                                patient via live video streaming if the clinical setting uses telehealth as part of its patient care offerings and if the patient is clinically appropriate to be seen via telehealth.  
                                <i>For all CSEs, please note that the ABPN-certified child and adolescent psychiatrist assessors must be physically present in the same room as the trainee for the entire patient 
                                evaluation, whether the patient is seen in person or remotely. As well, all patient evaluation must be with a live actual patient; videorecorded interactions or simulated/standardized 
                                patients cannot be used as the basis for the clinical skills evaluation.  The full CSE requirements document is located on the <a href="https://www.abpn.org/wp-content/uploads/2023/04/CSE-CAP.pdf" target="_blank">ABPN website</a>.</i></p>
                        </div>
                    </Fragment>);
        }
    }

    const clinicalSkillsColumns = React.useMemo(
        () => [
            {
                Header: "Evaluation",
                width: "200px",
                filterable: false,
                sortable: true,
                accessor: (cs: ClinicalSkill): any => {
                    if (cs.inEditMode) {
                        return (
                            <React.Fragment>
                                <select required id={evaluationFormId}
                                    className="form-control form-control-sm"
                                    data-msg="Reselection of CSE is required"
                                    defaultValue={cs.clinicalSkillTypeId != null ? data.filteredCseTypeListByProgram.find(_ => _.id === cs.clinicalSkillTypeId)?.id : ""}
                                    onChange={e => updateClinicalSkillType(e)} >
                                    <option value="">Select</option>
                                    {data.filteredCseTypeListByProgram.map((_, key) => (<option value={_.id} key={key}>{_.name}</option>))}
                                </select>
                                <span className="invalid-feedback"></span>
                            </React.Fragment>
                        )
                    }

                    return`${cs.clinicalSkillTypeDescription}`;
                }
            },
            {
                Header: "Program",
                width: "auto",
                filterable: false,
                sortable: false,
                filter: "fuzzyText",
                accessor: (cs: ClinicalSkill): string =>  `(${cs.trainingProgramCode}) ${cs.trainingProgramName1}`,
                id: "arClinicalSkillTrainingProgram"
            },
            {
                Header: "Passed",
                width: "120px",
                filterable: false,
                sortable: false,
                filter: "fuzzyText",
                id: "arPassedDate",
                accessor: (cs: ClinicalSkill) => {
                    let completedDate = cs.completedDate ? new Date(cs.completedDate) : null;
                    if (cs.inEditMode) {
                        return (
                            <div>
                                <DatePicker
                                    required
                                    className="bc-datepicker form-control form-control-sm"
                                    id={passedDateFormId}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    selected={completedDate}
                                    onChange={newDate => updateCompletedDate(newDate)}
                                    placeholderText="mm/dd/yyyy"
                                    disabled={!cs.clinicalSkillTypeId}/>
                                <span className="invalid-feedback"></span>
                            </div>
                        )
                    }
                    return completedDate ? <div className="text-start">{getDateWithOffset(cs.completedDate).toLocaleDateString()}</div> : null;
                },
            },
            {
                Header: "Evaluator",
                width: "220px",
                filterable: false,
                sortable: false,
                filter: "fuzzyText",
                id: "evaluator",
                accessor: (cs: ClinicalSkill) => {
                    if (cs.inEditMode) {
                        return (
                            <div>
                                <Autocomplete
                                    required
                                    selectedItem={cs.evaluator}
                                    name={evaluatorInputFormName}
                                    className="form-control form-control-sm"
                                    initialSelectedItem={getDefaultEvaluator(cs)}
                                    items={[getDefaultEvaluator(cs)]}
                                    itemKey={(item: PhysicianSearchResult) => item ? item.physicianId : ''}
                                    itemToString={(item: PhysicianSearchResult) => getEvaluatorDisplayName(item)}
                                    filter={(filter: string) => findEvaluators(filter, cs)}
                                    matchKeys={null}
                                    placeholder={"Enter Name..."}
                                    onChange={(selectedItem: PhysicianSearchResult) => updateEvaluator(selectedItem)}
                                    disabled={!cs.clinicalSkillTypeId || !cs.completedDate}
                                    minCharactersForSearch={3}>
                                </Autocomplete>
                                <span className="invalid-feedback"></span>
                            </div>
                        )
                    }
                    return `${cs.evaluatorName ? cs.evaluatorName : ""}`;
                },
            },
            {
                Header: "Attested",
                width: "100px",
                filterable: false,
                sortable: false,
                filter: 'fuzzyDate',
                id: "arIsAttested",
                accessor: (cs: ClinicalSkill) => {
                    return (cs.inEditMode || !cs.isAttestedComplete ) ? (
                        <Form.Check type="checkbox">
                            <Form.Check.Input checked={cs.isAttestedComplete}  defaultChecked={cs.isAttestedComplete} disabled={!user?.userRole?.isProgramDirector} className="pc-checkbox" type="checkbox" onChange={() => saveCseAttestation(cs)} />
                        </Form.Check>
                    ) :
                    (
                        <React.Fragment>
                            <div className="text-start">
                                { cs.isAttestedComplete ? "Yes" : "No" }
                                { cs.source === SourceType.ABPN &&
                                    <OverlayTrigger placement={"right"} overlay={<Tooltip id="isAttestedABPN" bsPrefix="pc-tooltip">CSE entered by ABPN.</Tooltip>}>
                                        <i className="las la-info-circle ml-1 ms-2 text-pc-secondary"></i>
                                    </OverlayTrigger>
                                }
                            </div>
                        </React.Fragment>
                    )
                }
            },
            {
                Header: " ",
                width: "100px",
                filterable: false,
                sortable: true,
                accessor: (cs: ClinicalSkill): any => {
                    if (cs.inEditMode) {
                        return (
                            <div>
                                <button type="button" title="Save" className="btn btn-clean btn-icon btn-icon-md" onClick={() => saveClinicalSkill()}>
                                    <i className="la la-save"></i>
                                </button>
                                <a title="Cancel" className="btn btn-clean btn-icon btn-icon-md"
                                    onClick={() => cancelEdit()}>
                                    <i className="la la-close"></i>
                                </a>
                            </div>
                        );
                    }
                    return (
                        <div>
                            <button type="button"
                                    hidden={isEditButtonHidden(cs) || props.programTypeIdsWithApprovedApplication.find(_ => _ === cs?.ownedByTrainingProgramTypeId) != null}
                                    title="Edit"
                                    className="btn btn-sm btn-clean btn-icon btn-icon-md"
                                    onClick={() => startEdit(cs)}
                                    >
                                <i className="la la-edit"></i>
                            </button>
                            <button type="button"
                                    title="Delete"
                                    hidden={isDeleteButtonHidden(cs) || props.programTypeIdsWithApprovedApplication.find(_ => _ === cs?.ownedByTrainingProgramTypeId) != null}
                                    className="btn btn-sm btn-clean btn-icon btn-icon-md"
                                    onClick={() => confirmDeleteClinicalSkill(cs, true)}>
                                <i className="la la-trash"></i>
                            </button>
                        </div>
                    )
                }
            }
        ],
        [data, props.isReadonly]
    );

    return (
        <>
            <h6 className="fw-bolder ml-2 mt-1 mb-4 text-secondary">Clinical Skills Evaluations
                <Button
                    className="ms-2 pc-button"
                    variant="primary" size="sm"
                    hidden={props.isReadonly || props.isAlumni || (data.clinicalSkills?.find(_ => _.inEditMode) != null) || props.programTypeIdsWithApprovedApplication.find(_ => _ === userSelectedTrainingProgram?.trainingProgramTypeId ) != null}
                    onClick={() => addClinicalSkill()}
                    disabled={!canAddClinicalSkill} title={!canAddClinicalSkill ? "Limit has been reached for this Clinical Skill Type." : ""}
                    >
                    Add CSE
                </Button>
            </h6>
            {showInvalidAttestationAlert &&
                <Alert variant={"danger"} onClose={() => setShowInvalidAttestationAlert(false)} dismissible>
                    <div>Invalid attestation:</div>
                    <>
                    {attestationValidationErrors.map((error, key) => {
                        return (
                            <Fragment key={key}>
                                <li className="ms-2 mt-1">{error}</li>
                            </Fragment>
                        );
                    })}
                    </>
                </Alert>
            }
            {!data.clinicalSkills?.length ? <span>No Clinical Skills</span> :
                <>
                    <Modal show={isConfirmingDelete} onHide={()=> confirmDeleteClinicalSkill(null, false)}>
                        <Modal.Body>Are you sure you want to delete this CSE?</Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={() => deleteClinicalSkill()}>
                                Yes, delete
                            </Button>
                            <Button variant="outline-secondary" onClick={() => confirmDeleteClinicalSkill(null, false)}>
                                No, Cancel
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Form id={clinicalSkillsForm} ref={editForm} noValidate>
                        <PreCertTable columns={clinicalSkillsColumns} data={data.clinicalSkills} getRowProps={(row: any) => getRowProps(row)} />
                    </Form>
                </>
            }
            {renderCseTeleDocInfo()}

            
        </>
    );
}